/**
 * Authentication Service - JWT Token Management
 * 
 * Handles login, token storage, and token refresh
 */
import { authAPI } from './api';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

export const authService = {
  /**
   * Login with username and password
   * Returns { success: boolean, token?: string, error?: string }
   */
  async login(username, password) {
    try {
      console.log('Attempting login for:', username);
      const response = await authAPI.login(username, password);
      console.log('Login response received:', response.status);
      
      const { access, refresh } = response.data;
      
      if (!access) {
        console.error('No access token in response:', response.data);
        return { 
          success: false, 
          error: 'Invalid response from server. Please try again.' 
        };
      }
      
      // Store tokens
      localStorage.setItem(TOKEN_KEY, access);
      if (refresh) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
      }
      
      // Store username for display
      localStorage.setItem('username', username);
      
      console.log('Login successful, token stored');
      return { success: true, token: access };
    } catch (error) {
      console.error('Login failed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle different error types
      let errorMessage = 'Login failed';
      
      if (error.response) {
        // Server responded with error
        const data = error.response.data;
        if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors && data.non_field_errors.length > 0) {
          errorMessage = data.non_field_errors[0];
        } else if (data.username) {
          errorMessage = `Username: ${Array.isArray(data.username) ? data.username[0] : data.username}`;
        } else if (data.password) {
          errorMessage = `Password: ${Array.isArray(data.password) ? data.password[0] : data.password}`;
        } else {
          errorMessage = `Server error (${error.response.status}). Please check your credentials.`;
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = 'Cannot connect to server. Please check your internet connection and try again.';
      } else {
        // Something else happened
        errorMessage = error.message || 'An unexpected error occurred';
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  },

  /**
   * Logout - clear tokens
   */
  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('username');
    window.location.href = '/';
  },

  /**
   * Get current access token
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Refresh access token using refresh token
   */
  async refreshToken() {
    try {
      const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refresh) {
        throw new Error('No refresh token available');
      }

      const response = await authAPI.refresh(refresh);
      const { access } = response.data;
      
      localStorage.setItem(TOKEN_KEY, access);
      return { success: true, token: access };
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear tokens on refresh failure
      this.logout();
      return { success: false, error: 'Token refresh failed' };
    }
  },

  /**
   * Get authorization header value
   */
  getAuthHeader() {
    const token = this.getToken();
    return token ? `Bearer ${token}` : null;
  }
};

export default authService;

